import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, query, onSnapshot, where } from '../firebase.config';
import { db } from '../firebase.config';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { getAuth } from 'firebase/auth';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';

const ResolvedIssues = () => {
  const { currentCompany, employees } = useEnhancedCompany();
  const auth = getAuth();
  const currentUser = auth.currentUser;

  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedPage, setSelectedPage] = useState('');
  const [selectedTimeRange, setSelectedTimeRange] = useState('');
  // Page options for filter
  const pageOptions = {
    'Business': {
      'Sales': ['Invoice', 'Challan', 'Quotation'],
      'Purchases': ['Purchase Bill', 'Purchase Order'],
      'Payments': ['Invoice', 'Challan', 'Purchase', 'Payment Receipts', 'Payment Mode'],
      'Expenses': ['Fixed', 'Variable', 'Labour/Advances', 'Employees', 'Salary'],
      'Manufacturing': ['Production Orders', 'Process Definitions', 'Process Directory', 'Analytics']
    },
    'Management': {
      'Items': [],
      'Parties': ['Parties Information', 'Parties Outstandings', 'Opening Balance Management'],
      'Bill Templates': []
    },
    'Reports': {
      'Business Reports': ['Partywise Sales Report', 'Customer Ledger Report', 'Invoice Collection Report', 'Payment Register Report', 'Aging Report', 'Itemwise Sales Report', 'Purchase Bills Summary', 'Stock Report'],
      'Tax Reports': ['GST Summary Report', 'GSTR-1 Report', 'GSTR-3B Report', 'HSN-wise GST Summary', 'Tax Payments Report'],
      'Financial Reports': ['Profit & Loss Report', 'Balance Sheet Report', 'Cash Flow Report'],
      'Data Export': ['Data Export Tools']
    },
    'System': {
      'Settings': ['Profile', 'Security & MPIN', 'MPIN Access Control', 'Payments', 'SMS', 'Branding'],
      'Company': ['Company Details', 'Company Settings'],
      'Help & Support': ['Help', 'FAQ', 'Contact Support'],
      'Image Manager': ['Image Management']
    }
  };

  // Time range options
  const timeRangeOptions = [
    { value: '', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' }
  ];

  // Load all resolved reports for the company
  useEffect(() => {
    if (!currentCompany?.companyId) return;

    const reportsRef = collection(db, 'companies', currentCompany.companyId, 'reports');
    const q = query(reportsRef, where('status', '==', 'resolved'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const reportsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by createdAt locally to avoid index requirement
      reportsData.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime; // Descending order
      });
      
      setReports(reportsData);
      setFilteredReports(reportsData);
      setLoading(false);
    }, (error) => {
      console.error('Error loading resolved reports:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentCompany?.companyId]);

  // Countdown functionality removed - resolved issues stay resolved

  // Filter reports based on selected filters
  useEffect(() => {
    let filtered = [...reports];

    // Filter by employee
    if (selectedEmployee) {
      filtered = filtered.filter(report => report.userId === selectedEmployee);
    }

    // Filter by page
    if (selectedPage) {
      filtered = filtered.filter(report => report.page === selectedPage);
    }

    // Filter by time range
    if (selectedTimeRange) {
      const now = new Date();
      let startDate;

      switch (selectedTimeRange) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'week':
          startDate = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'quarter':
          const quarter = Math.floor(now.getMonth() / 3);
          startDate = new Date(now.getFullYear(), quarter * 3, 1);
          break;
        case 'year':
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
        default:
          startDate = null;
      }

      if (startDate) {
        filtered = filtered.filter(report => {
          const reportDate = report.createdAt?.toDate ? report.createdAt.toDate() : new Date(report.createdAt?.seconds * 1000);
          return reportDate >= startDate;
        });
      }
    }

    setFilteredReports(filtered);
  }, [reports, selectedEmployee, selectedPage, selectedTimeRange]);

  // Get employee name by userId
  const getEmployeeName = (userId) => {
    const employee = employees?.find(emp => emp.userId === userId);
    return employee ? employee.name : 'Unknown Employee';
  };

  // Handle revert (move back to reported issues)
  const handleRevert = async (reportId) => {
    if (!window.confirm('Are you sure you want to revert this issue back to Reported Issues?')) return;

    try {
      const reportRef = doc(db, 'companies', currentCompany.companyId, 'reports', reportId);
      await updateDoc(reportRef, {
        status: 'pending',
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error reverting report:', error);
      alert('Error reverting report. Please try again.');
    }
  };

  // Handle delete (remove from database)
  const handleDelete = async (reportId) => {
    if (!window.confirm('Are you sure you want to permanently delete this resolved issue? This action cannot be undone.')) return;

    try {
      await deleteDoc(doc(db, 'companies', currentCompany.companyId, 'reports', reportId));
    } catch (error) {
      console.error('Error deleting report:', error);
      alert('Error deleting report. Please try again.');
    }
  };

  // Get all unique pages from reports
  const getUniquePages = () => {
    const pages = [...new Set(reports.map(report => report.page))];
    return pages.sort();
  };

  // Get all unique employees from reports
  const getUniqueEmployees = () => {
    const userIds = [...new Set(reports.map(report => report.userId))];
    return userIds;
  };

  // Countdown functionality removed - resolved issues stay resolved

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Resolved Issues</h1>
        <p className="text-gray-600">View and manage all resolved issues</p>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-green-600">{reports.length}</div>
            <div className="text-sm text-gray-600">Total Resolved</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-blue-600">{filteredReports.length}</div>
            <div className="text-sm text-gray-600">Filtered Results</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-purple-600">
              {reports.filter(r => {
                const reportDate = r.createdAt?.toDate ? r.createdAt.toDate() : new Date(r.createdAt?.seconds * 1000);
                const today = new Date();
                return reportDate.toDateString() === today.toDateString();
              }).length}
            </div>
            <div className="text-sm text-gray-600">Resolved Today</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Employee
            </label>
            <AppSelect
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              aria-label="Filter by employee"
              sheetTitle="Filter by employee"
              options={[
                { value: '', label: 'All Employees' },
                ...getUniqueEmployees().map(userId => ({
                  value: userId,
                  label: getEmployeeName(userId),
                })),
              ]}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER}`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Page/Feature
            </label>
            <AppSelect
              value={selectedPage}
              onChange={(e) => setSelectedPage(e.target.value)}
              aria-label="Filter by page"
              sheetTitle="Filter by page"
              options={[
                { value: '', label: 'All Pages' },
                ...getUniquePages().map(page => ({ value: page, label: page })),
              ]}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER}`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Time Range
            </label>
            <AppSelect
              value={selectedTimeRange}
              onChange={(e) => setSelectedTimeRange(e.target.value)}
              aria-label="Filter by time range"
              sheetTitle="Filter by time range"
              options={timeRangeOptions.map(option => ({ value: option.value, label: option.label }))}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER}`}
            />
          </div>
        </div>
      </div>

      {/* Reports List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {filteredReports.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            {reports.length === 0 ? 'No Resolved Issues Found' : 'No issues match the current filters'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Page/Feature
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Issue Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Owner Reply
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resolved Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {getEmployeeName(report.userId)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {report.userId.slice(0, 8)}...
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {report.page}
                      {report.subCategory && (
                        <div className="text-xs text-gray-500">{report.subCategory}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                      {report.billNo && <div className="font-medium">Bill: {report.billNo}</div>}
                      {report.reportType && <div className="font-medium">Report: {report.reportType}</div>}
                      {report.message && (
                        <div className="text-xs text-gray-600 mt-1 truncate">
                          {report.message}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {report.ownerReply ? (
                        <div>
                          <div className="text-blue-600">{report.ownerReply}</div>
                          {report.ownerReplyAt && (
                            <div className="text-xs text-gray-500">
                              {new Date(report.ownerReplyAt.seconds * 1000).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">No reply</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="text-green-600 font-medium">Resolved</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {report.updatedAt ? (
                        <div>
                          {new Date(report.updatedAt.seconds * 1000).toLocaleDateString()}
                          <div className="text-xs text-gray-500">
                            {new Date(report.updatedAt.seconds * 1000).toLocaleTimeString()}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">Unknown</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-y-2">
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => handleRevert(report.id)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Revert
                        </button>
                        <button
                          onClick={() => handleDelete(report.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResolvedIssues;
